import { useTranslation } from 'react-i18next';
import { haptic } from '../../../../lib/haptic';
import { DocPicker } from '../../../../components/form/DocPicker';
import { useSignup } from '../SignupContext';

const fieldCardStyle: React.CSSProperties = {
  background: 'var(--color-surface)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-card, 18px)',
  padding: '14px 16px',
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
};

const labelStyle: React.CSSProperties = {
  fontSize: '13px',
  fontWeight: 600,
  color: 'var(--color-text-secondary)',
  letterSpacing: '0.01em',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  border: 'none',
  outline: 'none',
  background: 'transparent',
  fontSize: '16px',
  color: 'var(--color-text)',
  padding: 0,
  fontFamily: 'inherit',
  minHeight: '24px',
};

const errorStyle: React.CSSProperties = {
  fontSize: '12px',
  color: '#B91C1C',
  margin: 0,
};

function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function DlStep() {
  const { t } = useTranslation('auth');
  const { state, setField, setStep } = useSignup();

  const dlNumber = state.dlNumber;
  const dlExpiry = state.dlExpiry;
  const dlPhoto = state.dlPhotoDataUrl;

  const today = todayIso();
  const dlValid = dlNumber.length >= 8 && dlNumber.length <= 16;
  const expiryValid = !!dlExpiry && dlExpiry > today;
  // Fields are optional for now — driver can submit KYC docs later via the
  // profile screen. We still show inline format errors when a value is
  // entered AND invalid, but empty values don't block progression.
  const dlOk = dlNumber.length === 0 || dlValid;
  const expiryOk = !dlExpiry || expiryValid;
  const canContinue = dlOk && expiryOk;

  const showDlError = dlNumber.length > 0 && !dlValid;
  const showExpiryError = !!dlExpiry && !expiryValid;

  const onContinue = () => {
    if (!canContinue) return;
    haptic.success();
    setStep('aadhaar');
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', paddingTop: '12px' }}>
      <div style={fieldCardStyle}>
        <label style={labelStyle} htmlFor="signup-dl-number">
          {t('signupFields.dlNumber')}
        </label>
        <input
          id="signup-dl-number"
          type="text"
          autoCapitalize="characters"
          autoCorrect="off"
          spellCheck={false}
          value={dlNumber}
          placeholder={t('signupFields.dlNumberPlaceholder')}
          onChange={(e) => setField('dlNumber', e.target.value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase().slice(0, 16))}
          style={{ ...inputStyle, letterSpacing: '0.04em', fontVariantNumeric: 'tabular-nums' }}
          maxLength={16}
        />
        {showDlError && (
          <p style={errorStyle}>{t('signupValidation.dlMin')}</p>
        )}
      </div>

      <div style={fieldCardStyle}>
        <label style={labelStyle} htmlFor="signup-dl-expiry">
          {t('signupFields.dlExpiry')}
        </label>
        <input
          id="signup-dl-expiry"
          type="date"
          value={dlExpiry}
          min={today}
          onChange={(e) => setField('dlExpiry', e.target.value)}
          style={inputStyle}
        />
        {showExpiryError && (
          <p style={errorStyle}>{t('signupValidation.dlExpired')}</p>
        )}
      </div>

      <div style={fieldCardStyle}>
        <DocPicker
          value={dlPhoto || null}
          onChange={(dataUrl) => setField('dlPhotoDataUrl', dataUrl ?? '')}
          label={t('signupFields.dlPhoto')}
          accept="image/*,application/pdf"
        />
      </div>

      <button
        type="button"
        onClick={onContinue}
        disabled={!canContinue}
        style={{
          marginTop: '8px',
          width: '100%',
          minHeight: 'var(--touch-target, 52px)',
          borderRadius: 'var(--radius-pill, 9999px)',
          border: 'none',
          background: canContinue ? 'var(--color-btn-primary)' : 'var(--color-border)',
          color: canContinue ? '#fff' : 'var(--color-text-secondary)',
          fontSize: '16px',
          fontWeight: 700,
          cursor: canContinue ? 'pointer' : 'not-allowed',
          transition: 'background-color 150ms ease, color 150ms ease',
          fontFamily: 'inherit',
        }}
      >
        {t('signupActions.continue')}
      </button>
    </div>
  );
}
